import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import { MouseEvent, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { MODALS } from 'src/models/ModalModel';
import { t } from 'src/lib/language';
import { FORM } from 'src/constants/Form';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { ENTITIES } from 'src/models/QuerybuilderModel';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { TicketModel, TicketStatus } from 'src/models/TicketModel';
import { ProductModel, ProductType } from 'src/models/ProductModel';
import { closeModal } from 'src/redux/actions/modalActions';
import { setError } from 'src/redux/actions/errorsActions';
import { setAlert } from 'src/redux/actions/alertActions';
import { postTicketAction } from 'src/redux/actions/ticketActions';
import { getFundraisingAction } from 'src/redux/actions/fundraisingAction';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import TicketValidator from 'src/validations/TicketValidator';
import ContactSelector from 'src/components/Control/ContactSelector';
import ProductsSelector from 'src/components/Control/ProductsSelector';
import IdentifierSelector from 'src/components/Control/IdentifierSelector';
import FormSingleSelect, { SelectItem } from 'src/components/Control/FormControls/FormSingleSelect';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import Header from 'src/components/UI/Header';
import TopRow from 'src/components/UI/TopRow';
import Title from 'src/components/UI/Title';
import CloseButton from 'src/components/UI/CloseButton';
import Scroller from 'src/components/UI/Scroller';
import Row from 'src/components/UI/Row';
import Col2 from 'src/components/UI/Col2';
import BottomRow from 'src/components/UI/BottomRow';

const AddTicketForm = () => {
  const dispatch = useAppDispatch();

  const { eventId } = useSelector(
    (state: Store) => state.modals[MODALS.addTicket]?.payload || {},
  );

  const products = useSelector((state: Store) => state.products);

  const lang = useSelector((state: Store) => state.language.language ?? 'en');

  const [ticketState, setTicketState] = useState<TicketModel>(
    { event_id: eventId } as TicketModel,
  );

  const fundraising = useSelector(
    (state: Store) => state.fundraisings[ticketState?.event_id],
  );

  useEffect(() => {
    if (ticketState?.event_id) {
      dispatch(getFundraisingAction(ticketState.event_id));
    }
  }, [ticketState?.event_id]);

  const selectedProduct = products.find(
    (product: ProductModel) => product.id === ticketState?.product_id,
  );
  const isIdentifierRequired = !!selectedProduct?.identifier_required;
  // The picker can only render seats when the product actually defines identifier rows.
  const canPickIdentifier = isIdentifierRequired && !!selectedProduct?.identifiers;
  const isFirstNameRequired = !!selectedProduct?.first_name_required;
  const isLastNameRequired = !!selectedProduct?.last_name_required;
  const isEmailRequired = !!selectedProduct?.email_required;
  const isPhoneRequired = !!selectedProduct?.phone_required;

  const getStatusItems = () => Object.values(TicketStatus).map((status) => ({
    id: status,
    text: status,
  } as SelectItem));

  const getEventLabel = () => {
    if (!ticketState?.event_id) return '';
    if (!fundraising?.fundraisingInfo?.name) return `#${ticketState.event_id}`;
    return `${fundraising.fundraisingInfo.name} (#${ticketState.event_id})`;
  };

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addTicket }));
  };

  const onChange = (value: Value, field: string) => {
    setTicketState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onSoldPriceChange = (value: Value, field: string) => {
    if (!value) {
      onChange(value, field);
      return;
    }
    const stringValue = (value as string) || '';
    if (!stringValue.match(/^[0-9.]+$/)) {
      return;
    }
    const splittedString = stringValue.split('.');
    if (splittedString.length > 2) {
      return;
    }
    if (splittedString.length === 2 && splittedString[1].length > 2) {
      return;
    }
    onChange(value, field);
  };

  const onSaved = () => {
    dispatch(closeModal({ modal: MODALS.addTicket }));
    dispatch(getEntitiesAction(ENTITIES.tickets));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.add_ticket]: {} }));
    try {
      TicketValidator(lang, isIdentifierRequired).validateSync(ticketState, { abortEarly: false });
    } catch (validationErrors: any) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: t(lang, 'validations.ticket.save_error'),
      }));
      const errorBag = {} as ErrorBag;
      validationErrors.inner.forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
      dispatch(setError({ [FORM.add_ticket]: errorBag }));
      return false;
    }

    dispatch(postTicketAction(ticketState, onSaved));
    return true;
  };

  const renderIdentifierField = () => {
    if (canPickIdentifier) {
      return (
        <IdentifierSelector
          identifiersString={selectedProduct?.identifiers || ''}
          eventId={ticketState?.event_id}
          productId={ticketState?.product_id}
          value={ticketState?.ticket_identifier || ''}
          onChange={(value: string) => onChange(value, 'ticket_identifier')}
          form={FORM.add_ticket}
          name="ticket_identifier" />
      );
    }
    return (
      <FormTextField
        form={FORM.add_ticket}
        label={t(lang, 'forms.tickets.ticket_identifier')}
        name="ticket_identifier"
        onChange={onChange}
        value={ticketState?.ticket_identifier || ''} />
    );
  };

  return (
    <div style={{ minHeight: '720px' }}>
      <Header height="48px">
        <TopRow>
          <Title>
            {t(lang, 'forms.tickets.add_title')}
          </Title>
          <div style={{ marginLeft: 'auto' }}>
            <CloseButton handleClose={handleClose} />
          </div>
        </TopRow>
      </Header>
      <Scroller>
        <Row>
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.event')}
              name="event_id"
              onChange={onChange}
              value={getEventLabel()}
              disabled />
          </Col2>
          <Col2>
            <ProductsSelector
              id="ticket-product"
              form={FORM.add_ticket}
              name="product_id"
              type={ProductType.ticket}
              onChange={(id: number) => onChange(id, 'product_id')}
              value={ticketState?.product_id} />
          </Col2>
        </Row>
        <Row style={{ marginTop: '-16px' }}>
          <Col2>
            <FormSingleSelect
              style={{ minWidth: '100%' }}
              form={FORM.add_ticket}
              id="ticket-status"
              items={getStatusItems()}
              label={t(lang, 'forms.tickets.status')}
              name="status"
              onChange={onChange}
              value={ticketState?.status || ''}
              noMarginTop />
          </Col2>
          <Col2>
            { renderIdentifierField() }
          </Col2>
        </Row>
        <Row height="96px" alignItems="start">
          <Col2>
            <ContactSelector
              autocompleteId={AUTOCOMPLETE_ID.add_ticket_contact}
              form={FORM.add_ticket}
              name="contact_id"
              label={t(lang, 'forms.tickets.contact')}
              onChange={(id: number) => onChange(id, 'contact_id')}
              contactId={ticketState?.contact_id}
              hideEdit={!ticketState?.contact_id} />
          </Col2>
          <Col2>
            <ContactSelector
              autocompleteId={AUTOCOMPLETE_ID.add_ticket_seller}
              form={FORM.add_ticket}
              name="seller_id"
              label={t(lang, 'forms.tickets.seller')}
              onChange={(id: number) => onChange(id, 'seller_id')}
              contactId={ticketState?.seller_id || undefined}
              hideEdit={!ticketState?.seller_id} />
          </Col2>
        </Row>
        { (isFirstNameRequired || isLastNameRequired) && (
        <Row>
          { isFirstNameRequired && (
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.first_name')}
              name="first_name"
              onChange={onChange}
              value={ticketState?.first_name || ''} />
          </Col2>
          )}
          { isLastNameRequired && (
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.last_name')}
              name="last_name"
              onChange={onChange}
              value={ticketState?.last_name || ''} />
          </Col2>
          )}
        </Row>
        )}
        { (isEmailRequired || isPhoneRequired) && (
        <Row style={{ marginTop: '-16px' }}>
          { isEmailRequired && (
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.email')}
              name="email"
              onChange={onChange}
              value={ticketState?.email || ''} />
          </Col2>
          )}
          { isPhoneRequired && (
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.phone')}
              name="phone"
              onChange={onChange}
              value={ticketState?.phone || ''} />
          </Col2>
          )}
        </Row>
        )}
        <Row style={{ marginTop: '-16px' }}>
          <Col2>
            <FormTextField
              form={FORM.add_ticket}
              label={t(lang, 'forms.tickets.sold_price')}
              name="sold_price"
              onChange={onSoldPriceChange}
              value={ticketState?.sold_price ?? ''} />
          </Col2>
          <Col2 />
        </Row>
        <BottomRow style={{ marginTop: '-16px' }}>
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: '12px' }}
            onClick={handleSubmit}
            key="submit-button">
            {t(lang, 'misc.save')}
          </Button>
        </BottomRow>
      </Scroller>
    </div>
  );
};

export default AddTicketForm;
